<template>
  <div class="tw-rounded-2xl tw-border tw-border-slate-200/70 tw-bg-white/70 tw-shadow-sm tw-shadow-slate-900/5 tw-backdrop-blur">
    <div class="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-slate-200/70 tw-px-4 tw-py-3">
      <h3 class="tw-text-sm tw-font-semibold tw-text-slate-900">Contacts</h3>
      <button
        v-if="!isEditingContact"
        type="button"
        class="tw-inline-flex tw-items-center tw-rounded-lg tw-bg-slate-900 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white hover:tw-bg-slate-800"
        @click="startContactEdit"
      >
        <font-awesome-icon icon="plus" class="tw-mr-2 tw-h-3.5 tw-w-3.5" />
        Add Contact
      </button>
    </div>

    <div class="tw-p-4 tw-space-y-4">
      <div class="tw-overflow-x-auto">
        <table class="tw-min-w-full tw-text-left tw-text-sm">
          <thead>
            <tr class="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500">
              <th class="tw-px-3 tw-py-2">Name</th>
              <th class="tw-px-3 tw-py-2">Phone Number</th>
              <th class="tw-px-3 tw-py-2">Email</th>
              <th class="tw-px-3 tw-py-2 tw-text-right">Actions</th>
            </tr>
          </thead>
          <tbody class="tw-divide-y tw-divide-slate-200/70">
            <tr v-for="contact in contacts" :key="contact.id" class="hover:tw-bg-slate-50/80">
              <td class="tw-px-3 tw-py-2 tw-align-middle">
                <EditableInputLabel
                  :modelValue="editingContactId === contact.id ? editForm.name : contact.name"
                  @update:modelValue="val => onEditFieldUpdate(contact.id, 'name', val)"
                  :isEditing="editingContactId === contact.id"
                />
              </td>
              <td class="tw-px-3 tw-py-2 tw-align-middle">
                <EditableInputLabel
                  :modelValue="editingContactId === contact.id ? editForm.phoneNumber : contact.phoneNumber"
                  @update:modelValue="val => onEditFieldUpdate(contact.id, 'phoneNumber', val)"
                  :isEditing="editingContactId === contact.id"
                />
              </td>
              <td class="tw-px-3 tw-py-2 tw-align-middle">
                <EditableInputLabel
                  :modelValue="editingContactId === contact.id ? editForm.email : contact.email"
                  @update:modelValue="val => onEditFieldUpdate(contact.id, 'email', val)"
                  :isEditing="editingContactId === contact.id"
                />
              </td>
              <td class="tw-px-3 tw-py-2 tw-align-middle">
                <div class="tw-flex tw-justify-end tw-gap-1.5">
                  <template v-if="editingContactId !== String(contact.id)">
                    <button
                      type="button"
                      class="tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-md tw-text-slate-500 hover:tw-bg-slate-100 hover:tw-text-slate-900"
                      v-tippy="'Edit'"
                      @click="toggleEditContact(contact)"
                    >
                      <font-awesome-icon icon="pen-to-square" class="tw-h-3.5 tw-w-3.5" />
                    </button>
                    <button
                      type="button"
                      class="tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-md tw-text-slate-500 hover:tw-bg-rose-50 hover:tw-text-rose-700"
                      v-tippy="'Delete'"
                      @click="deleteContact(String(contact.id))"
                    >
                      <font-awesome-icon icon="trash" class="tw-h-3.5 tw-w-3.5" />
                    </button>
                  </template>
                  <template v-else>
                    <button
                      type="button"
                      class="tw-inline-flex tw-items-center tw-rounded-md tw-bg-emerald-600 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-white hover:tw-bg-emerald-700"
                      @click="saveContactEdit"
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      class="tw-inline-flex tw-items-center tw-rounded-md tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-slate-700 hover:tw-bg-slate-50"
                      @click="cancelInlineEdit"
                    >
                      Cancel
                    </button>
                  </template>
                </div>
              </td>
            </tr>
            <tr v-if="!contacts || contacts.length === 0">
              <td colspan="4" class="tw-px-3 tw-py-4 tw-text-center tw-text-sm tw-text-slate-500">
                No contacts yet.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="isEditingContact" class="tw-mt-4 tw-rounded-xl tw-border tw-border-slate-200/80 tw-bg-slate-50/80 tw-p-4 tw-space-y-3">
        <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3">
          <div>
            <label for="new-contact-name" class="tw-block tw-text-xs tw-font-medium tw-text-slate-600">Name</label>
            <input
              id="new-contact-name"
              v-model="newContact.name"
              type="text"
              class="tw-mt-1 tw-w-full tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-900 placeholder:tw-text-slate-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500/40"
              placeholder="Name"
            />
          </div>
          <div>
            <label for="new-contact-phone" class="tw-block tw-text-xs tw-font-medium tw-text-slate-600">Phone number</label>
            <input
              id="new-contact-phone"
              v-model="newContact.phoneNumber"
              type="text"
              class="tw-mt-1 tw-w-full tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-900 placeholder:tw-text-slate-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500/40"
              placeholder="Phone number"
            />
          </div>
          <div>
            <label for="new-contact-email" class="tw-block tw-text-xs tw-font-medium tw-text-slate-600">Email</label>
            <input
              id="new-contact-email"
              v-model="newContact.email"
              type="text"
              class="tw-mt-1 tw-w-full tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-900 placeholder:tw-text-slate-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500/40"
              placeholder="Email"
            />
          </div>
        </div>

        <div class="tw-flex tw-justify-end tw-gap-2">
          <button
            type="button"
            class="tw-inline-flex tw-items-center tw-rounded-md tw-bg-slate-900 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-white hover:tw-bg-slate-800"
            @click="saveContact"
          >
            Save Contact
          </button>
          <Button
            :isGray="true"
            class="tw-inline-flex tw-items-center tw-rounded-md tw-bg-slate-600 tw-text-white hover:tw-bg-slate-700"
            @click="cancelEditingContact"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

import AddCustomerContact from '@/gql/customers/AddCustomerContact.gql';
import UpdateCustomerContact from '@/gql/customers/UpdateCustomerContact.gql';
import ContactsQuery from '@/gql/customers/query/contacts/Contacts.gql';
import DeleteContact from '@/gql/customers/mutations/contacts/Delete.gql';

import Button from '../core/Button.vue';
import EditableInputLabel from '../core/Input/EditableInputLabel.vue';
import type { Contact } from '../../types';

export default defineComponent({
  name: 'CustomerContacts',
  props: {
    customer: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      contacts: [] as Contact[],
      editingContactId: -1,
      isEditingName: false,
      isEditingAddress: false,
      isEditingContact: false,
      newContact: {} as Contact,
      editForm: {} as Contact,
    };
  },
  components: {
    Button,
    EditableInputLabel,
  },
  apollo: {
    contacts: {
      query: ContactsQuery,
      variables() {
        return {
          id: (this as any).customer.id,
        };
      },
      skip() {
        return !(this as any).customer;
      },
      update(data) {
        return data.customer.contacts;
      },
    },
  },
  methods: {
    onEditFieldUpdate(contactId: string | number, field: 'name' | 'phoneNumber' | 'email', value: string) {
      const vm = this as any;
      if (vm.editingContactId !== String(contactId)) return;
      vm.editForm = {
        ...vm.editForm,
        [field]: value,
      };
    },
    cancelInlineEdit() {
      (this as any).editingContactId = -1;
      (this as any).editForm = {} as Contact;
    },
    deleteContact(id: string) {
      (this as any).$apollo.mutate({
        mutation: DeleteContact,
        variables: {
          id,
        },
      });
    },
    toggleEditContact(contact: Contact) {
      (this as any).editingContactId = String(contact.id);
      (this as any).editForm = { ...contact };
    },
    startContactEdit() {
      (this as any).isEditingContact = true;
    },
    cancelEditingContact() {
      (this as any).isEditingContact = false;
    },
    refreshContacts() {
      (this as any).$apollo.queries.contacts.refresh();
    },
    saveContact() {
      const customerId = Number((this as any).customer?.id);
      (this as any).$apollo
        .mutate({
          mutation: AddCustomerContact,
          variables: {
            name: (this as any).newContact.name,
            phoneNumber: (this as any).newContact.phoneNumber,
            email: (this as any).newContact.email,
            customerId,
          },
        })
        .then((data) => {
          (this as any).isEditingContact = false;
          (this as any).newContact = {};
          (this as any).refreshContacts();
        })
        .catch((err) => {
          (this as any).isEditingContact = false;
        });
    },
    saveContactEdit() {
      const contact = (this as any).editForm;
      (this as any).$apollo
        .mutate({
          mutation: UpdateCustomerContact,
          variables: {
            name: contact.name,
            phoneNumber: contact.phoneNumber,
            email: contact.email,
            id: contact.id,
          },
        })
        .then((data) => {
          (this as any).editingContactId = -1;
          (this as any).refreshContacts();
        })
        .catch((err) => {
          // (this as any).isEditingContact = false;
        });
    },
  },
});
</script>

<style lang="scss">
</style>
